import { createGlobalStyle } from "styled-components";

const GlobalStyle = createGlobalStyle`
  .light-theme {
    --primary-color: #6366f1;
    --primary-color-light: #818cf8;
    --primary-color-dark: #4f46e5;
    --secondary-color: #ec4899;
    --background-dark-color: #fafafa;
    --background-dark-grey: #ffffff;
    --border-color: #e5e7eb;
    --background-light-color: #f3f4f6;
    --background-light-color-2: rgba(99, 102, 241, 0.1);
    --white-color: #111827;
    --font-light-color: #4b5563;
    --font-dark-color: #1f2937;
    --sidebar-dark-color: #ffffff;
    --scrollbar-bg-color: #f3f4f6;
    --scrollbar-thump-color: #d1d5db;
    --scrollbar-track-color: #f3f4f6;
    --card-bg: rgba(255, 255, 255, 0.8);
    --card-border: rgba(0, 0, 0, 0.06);
    --card-hover-border: rgba(99, 102, 241, 0.3);
    --shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.04);
    --shadow-md: 0 4px 12px rgba(0, 0, 0, 0.06);
    --shadow-lg: 0 10px 25px rgba(0, 0, 0, 0.08);
  }

  .dark-theme {
    --primary-color: #6366f1;
    --primary-color-light: #818cf8;
    --primary-color-dark: #4f46e5;
    --secondary-color: #ec4899;
    --background-dark-color: #09090b;
    --background-dark-grey: #111114;
    --border-color: #1c1c24;
    --background-light-color: #151518;
    --background-light-color-2: rgba(99, 102, 241, 0.12);
    --white-color: #f0f0f5;
    --font-light-color: #9898ad;
    --font-dark-color: #c0c0d0;
    --sidebar-dark-color: #0c0c0f;
    --scrollbar-bg-color: #111114;
    --scrollbar-thump-color: #2a2a3a;
    --scrollbar-track-color: #111114;
    --card-bg: rgba(17, 17, 20, 0.7);
    --card-border: rgba(255, 255, 255, 0.04);
    --card-hover-border: rgba(99, 102, 241, 0.4);
    --shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.2);
    --shadow-md: 0 4px 12px rgba(0, 0, 0, 0.3);
    --shadow-lg: 0 10px 25px rgba(0, 0, 0, 0.4);
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    list-style: none;
    text-decoration: none;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  }

  html {
    scroll-behavior: smooth;
  }

  body {
    background-color: var(--background-dark-color);
    color: var(--font-light-color);
    transition: background-color 0.3s ease, color 0.3s ease;
    font-size: 16px;
    line-height: 1.6;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  body::-webkit-scrollbar {
    width: 8px;
    background-color: var(--scrollbar-bg-color);
  }

  body::-webkit-scrollbar-thumb {
    border-radius: 10px;
    background-color: var(--scrollbar-thump-color);
  }

  body::-webkit-scrollbar-track {
    border-radius: 10px;
    background-color: var(--scrollbar-track-color);
  }

  textarea {
    max-width: 100%;
  }

  a {
    font-family: inherit;
    color: inherit;
    font-size: inherit;
  }

  h1 {
    font-size: 3.5rem;
    font-weight: 800;
    color: var(--white-color);
    line-height: 1.1;
    letter-spacing: -0.025em;
    @media screen and (max-width: 768px) {
      font-size: 2.5rem;
    }
    @media screen and (max-width: 480px) {
      font-size: 2rem;
    }
  }

  h2 {
    font-size: 2.25rem;
    font-weight: 700;
    color: var(--white-color);
    letter-spacing: -0.02em;
  }

  h3 {
    font-size: 1.5rem;
    font-weight: 600;
    color: var(--white-color);
  }

  h4 {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--white-color);
  }

  h5 {
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--white-color);
  }

  h6 {
    font-size: 0.95rem;
    font-weight: 600;
    color: var(--white-color);
    padding-bottom: 0.3rem;
  }

  span {
    color: var(--primary-color);
  }

  p {
    font-size: 0.95rem;
    line-height: 1.7;
  }

  ::selection {
    background-color: var(--primary-color);
    color: white;
  }

  /* Theme Toggle */
  .light-dark-mode {
    position: fixed;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    background-color: var(--card-bg);
    backdrop-filter: blur(12px);
    width: 2.75rem;
    height: 2.75rem;
    z-index: 15;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px 0 0 10px;
    cursor: pointer;
    transition: all 0.3s ease;
    border: 1px solid var(--border-color);
    border-right: none;
    &:hover {
      width: 3.25rem;
      background-color: var(--primary-color);
      border-color: var(--primary-color);
      svg {
        color: white;
      }
    }
    svg {
      color: var(--white-color);
      transition: color 0.3s ease;
    }
  }

  /* Hamburger Menu */
  .ham-burger-menu {
    position: fixed;
    right: 5%;
    top: 2rem;
    display: none;
    z-index: 25;
    cursor: pointer;
    padding: 0.6rem;
    border-radius: 10px;
    background: var(--card-bg);
    backdrop-filter: blur(12px);
    border: 1px solid var(--border-color);
    transition: all 0.3s ease;
    &:hover {
      background: var(--primary-color);
      border-color: var(--primary-color);
      svg {
        color: white;
      }
    }
    svg {
      color: var(--white-color);
      display: block;
      transition: color 0.3s ease;
    }
  }

  .nav-toggle {
    transform: translateX(0) !important;
    z-index: 20;
  }

  @media screen and (max-width: 1200px) {
    .ham-burger-menu {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
`;

export default GlobalStyle;
